'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import memoize from 'hs-lodash/memoize';
import once from 'hs-lodash/once';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useSelector } from 'react-redux';
import debounce from 'react-utils/debounce';
import * as CustomPropTypes from '../../../constants/PropTypes';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import UISelect from 'UIComponents/input/UISelect';
import * as GROUPED_CATEGORIES from 'ecosystem-marketplace-core/constants/groupTags/SuperCategoryGroup';
import { BY_CATEGORY as CATEGORY_FEATURES_BY_CATEGORY } from 'ecosystem-marketplace-core/constants/groupTags/categoryFeatures';
import { BY_PRODUCT as HUBSPOT_FEATURES_BY_PRODUCT } from 'ecosystem-marketplace-core/constants/groupTags/hubspotFeatures';
import { SUPER_CATEGORY_ORDER } from 'marketplace-ui-apps-core/common/constants/groupTags/SuperCategoryOrder';
import { getCategoryFeatureI18nKey, getSuperCategoryI18nKey } from 'marketplace-ui-apps-core/common/utils/i18n';
import { VIEW_ALL_APPS, useAppSearchBarQuery } from 'marketplace-ui-apps-core/search/hooks/useAppSearchBarQuery';
import { APPS_TYPEAHEAD_SEARCH_MIN_CHARS } from 'marketplace-ui-common/constants/search';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import InputHighlighter from 'marketplace-ui-common/search/components/InputHighlighter';
import SearchBar from 'marketplace-ui-common/search/components/SearchBar';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'marketplace-ui-common/search/utils';
import { getCategoryI18nKey, getHubSpotFeatureGroupI18nKey, getHubSpotFeatureI18nKey } from 'marketplace-ui-common/utils/i18n';
import * as FilterKey from '../../../constants/search/FilterKey';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import StorefrontFilters from '../../../records/StorefrontFilters';
import { getIsPublic, getIsUngatedForContentHub } from '../../../selectors/auth';
import { debug } from '../../../utils/env';
import { scrollToTop } from '../../../utils/scroll';
import MobileStorefrontSearchBar from '../results/mobile/MobileStorefrontSearchBar';
import { getIsUngatedForRecommendationsBadge } from '../../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
const CACHED_SEARCH_VALUES_KEY = 'ecosystem-marketplace-apps:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);
const EMPTY_SET = Immutable.Set();
const EMPTY_ORDERED_MAP = Immutable.OrderedMap();
const ALL_CATEGORIES_OPTION_VALUE = '__ALL_CATEGORIES__';
const getI18nKey = key => `appMarketplace.storefront.searchSelect.${key}`;
const getCategorySelectPlaceholder = () => {
  return unescapedText(getI18nKey('searchCategorySelectPlaceholder'));
};
const memoizeCategoryResolver = (superCategory, category) => [superCategory, category].filter(Boolean).join(':');
const getCategoryOptions = memoize((searchSuperCategory, searchCategory) => {
  if (searchCategory) {
    return EMPTY_SET;
  }
  return SUPER_CATEGORY_ORDER.reduce((final, superCategory) => {
    if (searchSuperCategory && searchSuperCategory !== superCategory) {
      return final;
    }
    const categories = GROUPED_CATEGORIES[superCategory];
    return final.withMutations(mutableMap => {
      categories.forEach(category => {
        if (mutableMap.has(category)) {
          return;
        }
        mutableMap.set(category, {
          text: unescapedText(getCategoryI18nKey(category)),
          value: `${FilterKey.CATEGORY}:${superCategory}:${category}`,
          filterKey: FilterKey.CATEGORY,
          help: !searchSuperCategory ? unescapedText(getI18nKey('inCategory'), {
            category: unescapedText(getSuperCategoryI18nKey(superCategory))
          }) : null
        });
      });
    });
  }, EMPTY_ORDERED_MAP).toOrderedSet();
}, memoizeCategoryResolver);
const getCategoryFeatureOptions = memoize((searchSuperCategory, searchCategory) => {
  return SUPER_CATEGORY_ORDER.reduce((final, superCategory) => {
    if (searchSuperCategory && searchSuperCategory !== superCategory) {
      return final;
    }
    const categories = Object.values(GROUPED_CATEGORIES[superCategory]).filter(category => searchCategory ? category === searchCategory : true);
    if (!categories.length) {
      return final;
    }
    return final.withMutations(mutableMap => {
      categories.forEach(category => {
        const features = Object.values(CATEGORY_FEATURES_BY_CATEGORY[category]);
        features.forEach(feature => {
          if (mutableMap.has(feature)) {
            return;
          }
          mutableMap.set(feature, {
            text: unescapedText(getCategoryFeatureI18nKey(category, feature)),
            value: `${FilterKey.CATEGORY_FEATURES}:${superCategory}:${category}:${feature}`,
            filterKey: FilterKey.CATEGORY_FEATURES,
            help: !searchCategory ? unescapedText(getI18nKey('inCategory'), {
              category: unescapedText(getCategoryI18nKey(category))
            }) : null
          });
        });
      });
    });
  }, EMPTY_ORDERED_MAP).toOrderedSet();
}, memoizeCategoryResolver);
const getHubSpotFeatureOptions = once(isUngatedForContentHub => Object.keys(HUBSPOT_FEATURES_BY_PRODUCT).reduce((result, hubSpotFeatureGroup) => {
  return result.concat(Object.values(HUBSPOT_FEATURES_BY_PRODUCT[hubSpotFeatureGroup]).map(hubSpotFeature => ({
    text: unescapedText(getHubSpotFeatureI18nKey(hubSpotFeature)),
    value: `${FilterKey.HUBSPOT_FEATURES}:${hubSpotFeature}`,
    filterKey: FilterKey.HUBSPOT_FEATURES,
    help: unescapedText(getI18nKey('inFeatureGroup'), {
      featureGroup: unescapedText(getHubSpotFeatureGroupI18nKey(hubSpotFeatureGroup, isUngatedForContentHub))
    })
  })));
}, Immutable.OrderedSet()));
const getMatchingOptions = (input, options) => {
  return options.filter(({
    text
  }) => {
    return UISelect.stripDiacritics(text.toLowerCase()).indexOf(input) !== -1;
  }).sortBy(({
    text
  }) => text).map(option => Object.assign({}, option, {
    dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
      text: option.text,
      value: input
    })
  })).toArray();
};
const getCategorySelectValue = (superCategory, category) => {
  if (superCategory && category) {
    return `${superCategory}:${category}`;
  } else if (superCategory) {
    return superCategory;
  }
  return ALL_CATEGORIES_OPTION_VALUE;
};
const getCategorySelectOptions = memoize(showHelpText => {
  const options = SUPER_CATEGORY_ORDER.reduce((final, superCategory) => {
    const categories = Immutable.Set(GROUPED_CATEGORIES[superCategory]);
    const superCategoryText = unescapedText(getSuperCategoryI18nKey(superCategory));
    final.push({
      text: superCategoryText,
      value: superCategory,
      dropdownText: /*#__PURE__*/_jsx(AbstractTextElement, {
        weight: "demi",
        children: superCategoryText
      })
    });
    return final.concat(categories.map(category => [category, unescapedText(getCategoryI18nKey(category))]).sortBy(([, categoryText]) => categoryText).map(([category, categoryText]) => ({
      text: categoryText,
      value: getCategorySelectValue(superCategory, category),
      dropdownClassName: 'p-left-4',
      help: showHelpText ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('inCategory'),
        options: {
          category: superCategoryText
        }
      }) : null
    })).toArray());
  }, []);
  const placeholder = getCategorySelectPlaceholder();
  return [{
    text: placeholder,
    dropdownText: /*#__PURE__*/_jsx(AbstractTextElement, {
      weight: "demi",
      children: placeholder
    }),
    value: ALL_CATEGORIES_OPTION_VALUE
  }, ...options];
});
const SearchSelect = ({
  activeCategory,
  activeSuperCategory,
  appIntegrations,
  inputSize,
  onAppSelect,
  onCategorySelect,
  onClear,
  onFeatureSelect,
  onFilterSelect,
  onSubmit,
  readOnly = false,
  searchValue
}) => {
  const isUngatedForContentHub = useSelector(getIsUngatedForContentHub);
  const isUngatedForRecommendationsBadge = useSelector(getIsUngatedForRecommendationsBadge);
  const cachedSearchValues = getCachedSearchValues();
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const [clearButtonClicked, setClearButtonClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [totalAppResults, setTotalAppResults] = useState(0);
  const [searchFilters, setSearchFilters] = useState(new StorefrontFilters({
    [FilterKey.SUPER_CATEGORY]: activeSuperCategory,
    [FilterKey.CATEGORY]: activeCategory,
    [FilterKey.SEARCH]: searchValue
  }));
  const {
    [FilterKey.SUPER_CATEGORY]: superCategory,
    [FilterKey.CATEGORY]: category,
    [FilterKey.SEARCH]: inputValue
  } = searchFilters;
  const prevInputValue = usePrevious(inputValue);
  const prevOpen = usePrevious(open);
  const prevCategory = usePrevious(category);
  const prevSuperCategory = usePrevious(superCategory);
  const prevActiveCategory = usePrevious(activeCategory);
  const prevActiveSuperCategory = usePrevious(activeSuperCategory);
  const prevSearchValue = usePrevious(searchValue);
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);

  // TODO track search term
  const trackSelectItem = useCallback(properties => {
    trackInteraction('select search autofill item', properties, 'storefront-select-search-autofill-item');
  }, [trackInteraction]);
  const handleResetOptions = () => {
    setOptions(null);
    setTotalAppResults(0);
  };
  const handleSetInputValue = useCallback(value => {
    setSearchFilters(searchFilters.set(FilterKey.SEARCH, value));
  }, [searchFilters]);
  const debouncedHandleSetInputValue = useMemo(() => debounce(handleSetInputValue, 250), [handleSetInputValue]);
  const [categorySelectValue, setCategorySelectValue] = useState(ALL_CATEGORIES_OPTION_VALUE);
  const handleSetCategorySelectValue = (newSuperCategory, newCategory) => {
    if (newSuperCategory && newCategory) {
      setCategorySelectValue(`${newSuperCategory}:${newCategory}`);
    } else if (newSuperCategory) {
      setCategorySelectValue(newSuperCategory);
    } else {
      setCategorySelectValue(ALL_CATEGORIES_OPTION_VALUE);
    }
  };
  const handleUpdateSearchFilters = useCallback((newSuperCategory, newCategory) => {
    const updatedSearchFilters = searchFilters.withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.SUPER_CATEGORY, newSuperCategory).set(FilterKey.CATEGORY, newCategory);
    });
    setSearchFilters(updatedSearchFilters);
    if (inputValue && !open) {
      onSubmit(updatedSearchFilters);
      handleSetCategorySelectValue(newSuperCategory, newCategory);
    } else {
      onCategorySelect(newSuperCategory, newCategory);
      handleSetCategorySelectValue(newSuperCategory, newCategory);
    }
    if (open && !inputValue) {
      setOpen(false);
    }
  }, [inputValue, onCategorySelect, onSubmit, open, searchFilters]);
  const handleCategorySelectChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (value === ALL_CATEGORIES_OPTION_VALUE) {
      handleUpdateSearchFilters();
    } else {
      const [optionSuperCategory, optionCategory] = value.split(':');
      handleUpdateSearchFilters(optionSuperCategory, optionCategory);
    }
    trackInteraction('select search category', {
      value
    }, 'storefront-select-search-category');
  }, [handleUpdateSearchFilters, trackInteraction]);
  const categorySelectProps = useMemo(() => {
    return {
      options: getCategorySelectOptions(),
      onChange: handleCategorySelectChange,
      value: categorySelectValue
    };
  }, [handleCategorySelectChange, categorySelectValue]);
  const defaultOptions = useMemo(() => {
    if (!cachedSearchValues.length) {
      return [];
    }
    return [{
      text: unescapedText(getI18nKey('groupLabels.recentSearches')),
      options: cachedSearchValues.map(value => ({
        text: value,
        value,
        cachedSearchValue: true,
        isSpecial: true
      }))
    }];
  }, [cachedSearchValues]);
  const integrationOptions = useMemo(() => appIntegrations.map(integration => ({
    text: integration,
    value: `${FilterKey.INTEGRATIONS}:${integration}`,
    filterKey: FilterKey.INTEGRATIONS
  })), [appIntegrations]);
  const isPublicApp = useSelector(getIsPublic);
  const {
    fetcher
  } = useAppSearchBarQuery({
    appContext: isPublicApp ? 'public' : 'app',
    grouped: true,
    query: {
      [FilterKey.SUPER_CATEGORY]: superCategory,
      [FilterKey.CATEGORY]: category
    },
    isUngatedForRecommendationsBadge
  });
  const getMatchingApps = useCallback(input => {
    return fetcher(input).then(matches => {
      if (!matches.length) {
        return null;
      }
      return matches[0];
    });
  }, [fetcher]);
  const getMatchingCategories = useCallback(input => {
    return Promise.resolve(getMatchingOptions(input, getCategoryOptions(superCategory, category))).then(matches => {
      if (!matches.length) {
        return null;
      }
      return {
        text: unescapedText(getI18nKey('groupLabels.categories')),
        options: matches
      };
    });
  }, [category, superCategory]);
  const getMatchingCategoryFeatures = useCallback(input => {
    return Promise.resolve(getMatchingOptions(input, getCategoryFeatureOptions(superCategory, category))).then(matches => {
      if (!matches.length) {
        return null;
      }
      return {
        text: unescapedText(getI18nKey('groupLabels.categoryFeatures')),
        options: matches
      };
    });
  }, [category, superCategory]);
  const getMatchingHubSpotFeatures = useCallback(input => {
    return Promise.resolve(getMatchingOptions(input, getHubSpotFeatureOptions(isUngatedForContentHub))).then(matches => {
      if (!matches.length) {
        return null;
      }
      return {
        text: unescapedText(getI18nKey('groupLabels.hubSpotFeatures')),
        options: matches
      };
    });
  }, [isUngatedForContentHub]);
  const getMatchingIntegrations = useCallback(input => {
    return Promise.resolve(getMatchingOptions(input, integrationOptions)).then(matches => {
      if (!matches.length) {
        return null;
      }
      return {
        text: unescapedText(getI18nKey('groupLabels.integrations')),
        options: matches
      };
    });
  }, [integrationOptions]);
  const handleSubmit = useCallback(value => {
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
    setOpen(false);
    onSubmit(searchFilters.set(FilterKey.SEARCH, value));
    setCachedSearchValue(value, getCachedSearchValues());
    scrollToTop();
  }, [onSubmit, searchFilters, trackInteraction]);
  const handleOpenChange = useCallback(openValue => {
    if (clearButtonClicked) {
      setClearButtonClicked(false);
      return;
    }
    setOpen(openValue);
  }, [clearButtonClicked]);
  const handleSearch = useCallback(input => {
    setOptions([]);
    setIsLoading(true);
    Promise.all([getMatchingApps(input), getMatchingCategories(input), getMatchingCategoryFeatures(input), getMatchingHubSpotFeatures(input), getMatchingIntegrations(input)]).then(([appsOptionGroup, categoriesOptionGroup, featuresOptionGroup, hubSpotFeaturesOptionGroup, integrationsOptionGroup]) => {
      setIsLoading(false);
      setOptions([appsOptionGroup, categoriesOptionGroup, featuresOptionGroup, hubSpotFeaturesOptionGroup, integrationsOptionGroup].filter(Boolean));
    }).catch(error => {
      console.error(error);
    });
  }, [getMatchingApps, getMatchingCategories, getMatchingCategoryFeatures, getMatchingHubSpotFeatures, getMatchingIntegrations]);
  const handleSelectedOptionChange = useCallback(({
    target: {
      value: option
    }
  }) => {
    const {
      value,
      filterKey,
      cachedSearchValue
    } = option;
    if (filterKey) {
      const [, ...filterValues] = value.split(':');
      switch (filterKey) {
        case FilterKey.CATEGORY:
          {
            const [optionSuperCategory, optionCategory] = filterValues;
            onCategorySelect(optionSuperCategory, optionCategory);
            trackSelectItem({
              type: 'category',
              value: optionCategory
            });
            break;
          }
        case FilterKey.CATEGORY_FEATURES:
          {
            const [optionSuperCategory, optionCategory, optionFeature] = filterValues;
            onFeatureSelect(optionSuperCategory, optionCategory, optionFeature);
            trackSelectItem({
              type: 'category feature',
              value: optionFeature
            });
            break;
          }
        case FilterKey.HUBSPOT_FEATURES:
          {
            const filterValue = filterValues[0];
            onFilterSelect(filterKey, filterValue);
            trackSelectItem({
              type: 'hubspot feature',
              value: filterValue
            });
            break;
          }
        case FilterKey.INTEGRATIONS:
          {
            const filterValue = filterValues[0];
            onFilterSelect(filterKey, filterValue);
            trackSelectItem({
              type: 'integration',
              value: filterValue
            });
            break;
          }
        default:
          break;
      }
      return;
    }
    if (cachedSearchValue) {
      handleSubmit(value);
      trackSelectItem({
        type: 'recent search',
        value
      });
      return;
    }
    if (value === VIEW_ALL_APPS) {
      handleSubmit(inputValue);
      trackSelectItem({
        type: 'see more',
        totalResults: totalAppResults
      });
      return;
    }
    const {
      slug,
      category: appCategory,
      superCategory: appSuperCategory,
      recommended
    } = option;
    onAppSelect(slug, appCategory, appSuperCategory);
    trackSelectItem({
      type: 'app',
      value: slug,
      isRecommended: recommended
    });
  }, [handleSubmit, inputValue, onAppSelect, onCategorySelect, onFeatureSelect, onFilterSelect, totalAppResults, trackSelectItem]);
  useEffect(() => {
    if (inputValue !== prevInputValue || open !== prevOpen || isMobileView && inputValue !== prevInputValue) {
      if ((open || isMobileView) && inputValue.length >= APPS_TYPEAHEAD_SEARCH_MIN_CHARS) {
        handleSearch(inputValue);
      } else {
        handleResetOptions();
      }
    }
  }, [inputValue, handleSearch, isMobileView, open, prevInputValue, prevOpen]);
  useEffect(() => {
    if (category !== prevCategory || superCategory !== prevSuperCategory) {
      if (open && inputValue) {
        handleSearch(inputValue);
      } else {
        handleResetOptions();
      }
    }
  }, [category, inputValue, handleSearch, open, prevCategory, prevSuperCategory, superCategory]);
  useEffect(() => {
    if (activeCategory !== prevActiveCategory || activeSuperCategory !== prevActiveSuperCategory || searchValue !== prevSearchValue) {
      if (activeCategory !== category || activeSuperCategory !== superCategory) {
        setOpen(false);
      }
      handleSetCategorySelectValue(activeSuperCategory, activeCategory);
      setSearchFilters(searchFilters.withMutations(mutableFilters => {
        mutableFilters.set(FilterKey.SUPER_CATEGORY, activeSuperCategory).set(FilterKey.CATEGORY, activeCategory).set(FilterKey.SEARCH, open ? inputValue : searchValue);
      }));
    }
  }, [activeCategory, activeSuperCategory, category, inputValue, open, prevActiveCategory, prevActiveSuperCategory, prevSearchValue, searchFilters, searchValue, superCategory]);
  useEffect(() => {
    if (debug('search-select')) {
      console.groupCollapsed('storefront search select');
      console.log('activeSuperCategory:', activeSuperCategory);
      console.log('superCategory:', superCategory);
      console.log('activeCategory:', activeCategory);
      console.log('category:', category);
      console.log('inputValue:', inputValue);
      console.log('isLoading:', isLoading);
      console.log('open:', open);
      console.log('options:', options);
      console.log('totalAppResults:', totalAppResults);
      console.groupEnd();
    }
  }, [activeCategory, activeSuperCategory, totalAppResults, category, isLoading, options, superCategory, open, inputValue]);
  return /*#__PURE__*/_jsx(MediaQuery, {
    minDeviceSize: "sm",
    alternate: /*#__PURE__*/_jsx(MobileStorefrontSearchBar, {
      onClearSearch: onClear,
      onInputValueChange: ({
        target: {
          value
        }
      }) => debouncedHandleSetInputValue(value),
      onSearchSubmit: handleSubmit,
      onSelectedOptionChange: handleSelectedOptionChange,
      placeholder: unescapedText(getI18nKey('searchInputPlaceholder')),
      searchTerm: inputValue,
      typeaheadResults: options || []
    }),
    children: /*#__PURE__*/_jsx(SearchBar, {
      categorySelectProps: categorySelectProps,
      inputSize: inputSize,
      inputValue: inputValue,
      isLoading: isLoading,
      onClear: onClear,
      onOpenChange: handleOpenChange,
      onInputValueChange: ({
        target: {
          value
        }
      }) => debouncedHandleSetInputValue(value),
      onSearchSubmit: handleSubmit,
      options: options || defaultOptions,
      onSelectedOptionChange: handleSelectedOptionChange,
      placeholder: unescapedText(getI18nKey('searchInputPlaceholder')),
      readOnly: readOnly
    })
  });
};
SearchSelect.propTypes = {
  activeCategory: CustomPropTypes.category,
  activeSuperCategory: CustomPropTypes.superCategory,
  appIntegrations: ImmutablePropTypes.orderedSet.isRequired,
  inputSize: PropTypes.string.isRequired,
  onAppSelect: PropTypes.func.isRequired,
  onCategorySelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onFeatureSelect: PropTypes.func.isRequired,
  onFilterSelect: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  searchValue: PropTypes.string
};
export default SearchSelect;